/**
 * AdminAnalytics - BI Dashboard for DexRiddle
 * Route: /admin/analytics
 * Supports real Neon/PostgreSQL data with SQL hourly aggregation.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ReferenceLine,
} from 'recharts';
import {
  Users, Activity, TrendingUp, TrendingDown, Clock,
  Globe, Zap, BarChart2, Menu, X, Lock, Eye, EyeOff, RefreshCw,
  Calendar, Radio, TableProperties,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';

// ---------------------------------------------------------------------------
// Mock data (deterministic — no randomness at render time)
// ---------------------------------------------------------------------------
function buildMockData() {
  const startDate = new Date('2026-01-20');
  const days = 30;

  // Pass 1: core metrics
  const dailyData = Array.from({ length: days }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const base = isWeekend ? 500 : 350;
    const visitors = Math.max(200, Math.min(800,
      Math.floor(base + Math.sin(i * 0.7) * 120 + Math.cos(i * 1.3) * 80)
    ));
    const sessions = Math.floor(visitors * (1.2 + Math.sin(i) * 0.1));
    return {
      date: date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
      dateISO: date.toISOString().split('T')[0],
      visitors,
      sessions,
      bounceRate: Math.round(35 + Math.sin(i * 0.5) * 8),
      avgDuration: Math.round(180 + Math.cos(i * 0.4) * 40),
      responseTime: Math.round(120 + Math.sin(i * 0.8) * 30),
      cacheHitRate: Math.round(78 + Math.sin(i * 0.6) * 8),
      apiCalls: Math.floor(visitors * 4.5 + Math.cos(i) * 150),
      priorVisitors: 0, // filled in pass 2
    };
  });

  // Pass 2: prior-week comparison
  dailyData.forEach((d, i) => {
    d.priorVisitors = i >= 7
      ? dailyData[i - 7].visitors
      : Math.floor(d.visitors * 0.88);
  });

  const totalVisitors = dailyData.reduce((s, d) => s + d.visitors, 0);
  const totalSessions = dailyData.reduce((s, d) => s + d.sessions, 0);
  const avgBounce = Math.round(dailyData.reduce((s, d) => s + d.bounceRate, 0) / days);
  const avgDuration = Math.round(dailyData.reduce((s, d) => s + d.avgDuration, 0) / days);

  const firstHalfV = dailyData.slice(0, 15).reduce((s, d) => s + d.visitors, 0);
  const secondHalfV = dailyData.slice(15).reduce((s, d) => s + d.visitors, 0);
  const visitorsChange = Math.round(((secondHalfV - firstHalfV) / firstHalfV) * 100);

  return {
    dailyData,
    kpis: {
      visitors: { value: totalVisitors, change: visitorsChange },
      sessions: { value: totalSessions, change: visitorsChange + 2 },
      bounceRate: { value: avgBounce, change: -3 },
      avgDuration: { value: avgDuration, change: 5 },
    },
    topRoutes: [
      { path: '/', name: 'Inicio', visits: 12400, pct: 31 },
      { path: '/pokemon/:id', name: 'Detalle Pokémon', visits: 9800, pct: 24 },
      { path: '/dexbuilder', name: 'DexBuilder', visits: 6200, pct: 15 },
      { path: '/calculator', name: 'Calculadora', visits: 4100, pct: 10 },
      { path: '/types', name: 'Tipos', visits: 3300, pct: 8 },
      { path: '/comparator', name: 'Comparador', visits: 2700, pct: 7 },
      { path: '/items', name: 'Objetos', visits: 1400, pct: 3 },
      { path: '/favorites', name: 'Favoritos', visits: 800, pct: 2 },
    ],
    funnel: [
      { name: 'Landing', value: 40000, fill: '#7C3AED' },
      { name: language === 'es' ? 'Búsqueda' : 'Search', value: 28000, fill: '#8B5CF6' },
      { name: language === 'es' ? 'Calculadora' : 'Calculator', value: 8200, fill: '#A78BFA' },
      { name: language === 'es' ? 'Exportar' : 'Export', value: 2100, fill: '#C4B5FD' },
    ],
    countries: [
      { name: 'España', flag: '🇪🇸', pct: 40 },
      { name: 'México', flag: '🇲🇽', pct: 25 },
      { name: 'Argentina', flag: '🇦🇷', pct: 15 },
      { name: 'Colombia', flag: '🇨🇴', pct: 10 },
      { name: 'USA', flag: '🇺🇸', pct: 10 },
    ],
    devices: [
      { name: 'Mobile', value: 55, fill: '#7C3AED' },
      { name: 'Desktop', value: 35, fill: '#E11D48' },
      { name: 'Tablet', value: 10, fill: '#F59E0B' },
    ],
    browsers: [
      { name: 'Chrome', pct: 58 },
      { name: 'Safari', pct: 22 },
      { name: 'Firefox', pct: 12 },
      { name: 'Edge', pct: 8 },
    ],
    topPokemon: [
      { name: 'Pikachu', views: 4200 },
      { name: 'Charizard', views: 3800 },
      { name: 'Mewtwo', views: 3100 },
      { name: 'Eevee', views: 2900 },
      { name: 'Gengar', views: 2600 },
      { name: 'Lucario', views: 2400 },
      { name: 'Garchomp', views: 2100 },
      { name: 'Umbreon', views: 1900 },
      { name: 'Gardevoir', views: 1700 },
      { name: 'Sylveon', views: 1500 },
    ],
    topMoves: [
      { name: 'Surf', count: 8900 },
      { name: 'Earthquake', count: 7600 },
      { name: 'Thunderbolt', count: 6800 },
      { name: 'Ice Beam', count: 5400 },
    ],
    topItems: [
      { name: 'Leftovers', count: 5100 },
      { name: 'Choice Band', count: 4200 },
      { name: 'Life Orb', count: 3900 },
    ],
    // Hourly data mock (today vs yesterday)
    hourlyData: Array.from({ length: 24 }, (_, h) => ({
      hour: `${String(h).padStart(2, '0')}:00`,
      today: h <= 14
        ? Math.max(0, Math.floor(12 + Math.sin(h * 0.55) * 10 + (h >= 9 && h <= 14 ? 28 : 0)))
        : 0,
      yesterday: Math.max(0, Math.floor(10 + Math.sin(h * 0.55 + 0.4) * 9 + (h >= 9 && h <= 21 ? 22 : 0))),
    })),
    // KPI comparisons mock
    kpiComparisons: {
      today_views: 342,
      yesterday_views: 318,
      last_7d_views: 2156,
      last_30d_views: 8840,
      today_sessions: 89,
      yesterday_sessions: 82,
      today_custom_events: 620,
      yesterday_custom_events: 575,
      today_bounce_rate: 38,
      yesterday_bounce_rate: 41,
    },
    // Top events breakdown mock
    topEvents: [
      { name: 'search',           count: 1240, pct: 35 },
      { name: 'pokemon_view',     count: 890,  pct: 25 },
      { name: 'calculator_used',  count: 420,  pct: 12 },
      { name: 'team_exported',    count: 210,  pct:  6 },
      { name: 'item_view',        count: 180,  pct:  5 },
      { name: 'nature_lookup',    count: 155,  pct:  4 },
      { name: 'team_link_copied', count: 98,   pct:  3 },
      { name: 'favorite_added',   count: 87,   pct:  2 },
      { name: 'comparator_used',  count: 76,   pct:  2 },
      { name: 'type_chart_viewed',count: 68,   pct:  2 },
    ],
  };
}

// language placeholder for funnel labels — resolved in component
const language = 'es';
const MOCK_DATA = buildMockData();

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------
const SIDEBAR_ITEMS = [
  { id: 'dashboard', icon: BarChart2, labelEs: 'Dashboard',    labelEn: 'Dashboard' },
  { id: 'traffic',   icon: Activity,  labelEs: 'Tráfico',      labelEn: 'Traffic' },
  { id: 'geo',       icon: Globe,     labelEs: 'Geo & Tech',   labelEn: 'Geo & Tech' },
  { id: 'pokemon',   icon: TrendingUp, labelEs: 'Pokémon Meta', labelEn: 'Pokémon Meta' },
  { id: 'health',    icon: Zap,       labelEs: 'Sistema',      labelEn: 'Health' },
];

function SidebarNav({ activeTab, setTab, expanded, onToggle, lang }) {
  return (
    <aside
      data-testid="admin-sidebar"
      className={cn(
        'h-full bg-card border-r border-border flex flex-col transition-all duration-300 shrink-0',
        expanded ? 'w-60' : 'w-16'
      )}
    >
      <div className="h-16 flex items-center justify-end px-3 border-b border-border">
        <Button variant="ghost" size="icon" onClick={onToggle} data-testid="sidebar-toggle">
          {expanded ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </Button>
      </div>

      <nav className="flex-1 py-4 space-y-1 px-2">
        {SIDEBAR_ITEMS.map(({ id, icon: Icon, labelEs, labelEn }) => {
          const isActive = activeTab === id;
          const label = lang === 'es' ? labelEs : labelEn;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`sidebar-tab-${id}`}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-medium text-left',
                isActive
                  ? 'bg-gradient-to-r from-violet-600 to-rose-500 text-white shadow-md'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              )}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {expanded && (
                <span className="truncate">{label}</span>
              )}
            </button>
          );
        })}
      </nav>

      {expanded && (
        <div className="p-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            <span className="text-violet-600 font-semibold">Dex</span>
            <span className="text-rose-500 font-semibold">Riddle</span>
            {' '}Analytics
          </p>
        </div>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// DateRangePicker — preset buttons + Yesterday + custom range
// ---------------------------------------------------------------------------
const DATE_RANGES = [
  { id: 'today',     labelEs: 'Hoy',   labelEn: 'Today' },
  { id: 'yesterday', labelEs: 'Ayer',  labelEn: 'Yesterday' },
  { id: '7d',        labelEs: '7d',    labelEn: '7d' },
  { id: '30d',       labelEs: '30d',   labelEn: '30d' },
  { id: 'month',     labelEs: 'Mes',   labelEn: 'Month' },
  { id: 'custom',    labelEs: 'Rango', labelEn: 'Range' },
];

function DateRangePicker({ lang, active, setActive, customFrom, customTo, setCustomFrom, setCustomTo }) {
  const [showCustom, setShowCustom] = useState(active === 'custom');

  const handlePreset = (id) => {
    if (id === 'custom') {
      setShowCustom(true);
      setActive('custom');
    } else {
      setShowCustom(false);
      setActive(id);
    }
  };

  const handleCustomApply = () => {
    if (customFrom && customTo) setActive('custom');
  };

  return (
    <div className="mb-6 space-y-3" data-testid="date-range-picker">
      <div className="flex flex-wrap items-center gap-2">
        {DATE_RANGES.map(({ id, labelEs, labelEn }) => (
          <Button
            key={id}
            size="sm"
            variant={active === id ? 'default' : 'outline'}
            onClick={() => handlePreset(id)}
            data-testid={`date-range-${id}`}
            className={cn(
              'text-xs',
              active === id && 'bg-gradient-to-r from-violet-600 to-rose-500 text-white border-0'
            )}
          >
            {id === 'custom' && <Calendar className="w-3 h-3 mr-1" />}
            {lang === 'es' ? labelEs : labelEn}
          </Button>
        ))}
      </div>

      {/* Custom range inputs */}
      {showCustom && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="flex flex-wrap items-center gap-3"
        >
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">
              {lang === 'es' ? 'Desde' : 'From'}
            </label>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="text-xs border border-input rounded-md px-2 py-1 bg-background outline-none focus:border-violet-500"
              data-testid="custom-range-from"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">
              {lang === 'es' ? 'Hasta' : 'To'}
            </label>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="text-xs border border-input rounded-md px-2 py-1 bg-background outline-none focus:border-violet-500"
              data-testid="custom-range-to"
            />
          </div>
          <Button
            size="sm"
            onClick={handleCustomApply}
            disabled={!customFrom || !customTo}
            className="text-xs bg-gradient-to-r from-violet-600 to-rose-500 text-white border-0"
            data-testid="custom-range-apply"
          >
            {lang === 'es' ? 'Aplicar' : 'Apply'}
          </Button>
        </motion.div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPICard with sparkline + "vs Ayer" comparison
// ---------------------------------------------------------------------------
function KPICard({ title, value, change, sparklineData, icon: Icon, format, yesterdayValue, lang }) {
  const isPositive = change >= 0;
  let formattedValue;
  if (format === 'duration') {
    formattedValue = `${Math.floor(value / 60)}m ${value % 60}s`;
  } else if (format === 'percent') {
    formattedValue = `${value}%`;
  } else {
    formattedValue = value.toLocaleString('es-ES');
  }

  // vs yesterday
  let vsAyer = null;
  if (yesterdayValue != null && yesterdayValue > 0) {
    const diffPct = Math.round(((value - yesterdayValue) / yesterdayValue) * 100);
    const sign = diffPct >= 0 ? '+' : '';
    const col = diffPct >= 0 ? 'text-emerald-600' : 'text-rose-500';
    const fmtYest = format === 'percent'
      ? `${yesterdayValue}%`
      : yesterdayValue.toLocaleString('es-ES');
    vsAyer = { text: `${fmtYest} (${sign}${diffPct}%)`, colorClass: col };
  }

  // Safe id for SVG gradient
  const gradId = `spark-${title.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}`;

  return (
    <Card data-testid={`kpi-card-${title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
              {title}
            </p>
            <p className="text-2xl font-bold mt-1">{formattedValue}</p>
            <div className={cn(
              'flex items-center gap-1 mt-1 text-xs font-medium',
              isPositive ? 'text-emerald-600' : 'text-rose-500'
            )}>
              {isPositive
                ? <TrendingUp className="w-3 h-3" />
                : <TrendingDown className="w-3 h-3" />
              }
              {Math.abs(change)}% vs {lang === 'es' ? 'período anterior' : 'prior period'}
            </div>
            {vsAyer && (
              <p className={cn('text-xs mt-0.5', vsAyer.colorClass)}>
                {lang === 'es' ? 'vs Ayer' : 'vs Yesterday'}: {vsAyer.text}
              </p>
            )}
          </div>
          <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <Icon className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          </div>
        </div>

        {/* Sparkline */}
        <ResponsiveContainer width="100%" height={32}>
          <AreaChart data={sparklineData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="visitors"
              stroke="#7C3AED"
              strokeWidth={1.5}
              fill={`url(#${gradId})`}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TrafficChart — master AreaChart
// ---------------------------------------------------------------------------
function TrafficChart({ data, lang }) {
  return (
    <Card className="col-span-full" data-testid="traffic-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          {lang === 'es' ? 'Visitas Diarias vs Semana Pasada' : 'Daily Visits vs Last Week'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="visitGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="priorGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#E11D48" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#E11D48" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              interval={4}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.75rem',
                fontSize: '12px',
              }}
              labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="visitors"
              name={lang === 'es' ? 'Esta semana' : 'This week'}
              stroke="#7C3AED"
              strokeWidth={2}
              fill="url(#visitGrad)"
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="priorVisitors"
              name={lang === 'es' ? 'Semana pasada' : 'Last week'}
              stroke="#E11D48"
              strokeWidth={1.5}
              strokeDasharray="4 2"
              fill="url(#priorGrad)"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// HourlyComparisonChart — dual-line: Hoy (solid) vs Ayer (dashed)
// ---------------------------------------------------------------------------
function HourlyComparisonChart({ data, lang }) {
  const now = new Date();
  const currentHour = now.getHours();

  return (
    <Card className="col-span-full" data-testid="hourly-comparison-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          {lang === 'es' ? 'Visitas por Hora: Hoy vs Ayer' : 'Hourly Traffic: Today vs Yesterday'}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {lang === 'es'
            ? `Datos hasta las ${String(currentHour).padStart(2, '0')}:00 UTC`
            : `Data up to ${String(currentHour).padStart(2, '0')}:00 UTC`}
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="hour"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              interval={2}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={35}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.75rem',
                fontSize: '12px',
              }}
              labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
            />
            <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
            {/* Reference line at current hour */}
            <ReferenceLine
              x={`${String(currentHour).padStart(2, '0')}:00`}
              stroke="#7C3AED"
              strokeDasharray="4 2"
              strokeOpacity={0.5}
            />
            {/* Ayer — dashed, tenue */}
            <Line
              type="monotone"
              dataKey="yesterday"
              name={lang === 'es' ? 'Ayer' : 'Yesterday'}
              stroke="#E11D48"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              dot={false}
              strokeOpacity={0.6}
            />
            {/* Hoy — solid, prominente */}
            <Line
              type="monotone"
              dataKey="today"
              name={lang === 'es' ? 'Hoy' : 'Today'}
              stroke="#7C3AED"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, fill: '#7C3AED' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TopEventsTable — event breakdown with count + % of total
// ---------------------------------------------------------------------------
function TopEventsTable({ data, lang }) {
  const EVENT_LABELS = {
    search:            { es: 'Búsqueda',         en: 'Search' },
    pokemon_view:      { es: 'Vista Pokémon',     en: 'Pokémon View' },
    calculator_used:   { es: 'Calculadora',       en: 'Calculator' },
    team_exported:     { es: 'Team Exportado',    en: 'Team Exported' },
    item_view:         { es: 'Vista Objeto',      en: 'Item View' },
    nature_lookup:     { es: 'Consulta Natura',   en: 'Nature Lookup' },
    team_link_copied:  { es: 'Link Copiado',      en: 'Link Copied' },
    favorite_added:    { es: 'Favorito',          en: 'Favorited' },
    comparator_used:   { es: 'Comparador',        en: 'Comparator' },
    type_chart_viewed: { es: 'Tabla de Tipos',    en: 'Type Chart' },
    move_selected:     { es: 'Movimiento sel.',   en: 'Move Selected' },
    item_selected:     { es: 'Objeto sel.',       en: 'Item Selected' },
    csv_export:        { es: 'CSV Exportado',     en: 'CSV Export' },
  };

  const total = data.reduce((s, e) => s + e.count, 0) || 1;

  return (
    <Card data-testid="top-events-table">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <TableProperties className="w-4 h-4 text-violet-600" />
          <CardTitle className="text-base">
            {lang === 'es' ? 'Top Eventos' : 'Top Events'}
          </CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          {total.toLocaleString('es-ES')} {lang === 'es' ? 'eventos totales' : 'total events'}
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-4 text-xs text-muted-foreground font-medium">
                {lang === 'es' ? 'Evento' : 'Event'}
              </th>
              <th className="text-right py-2 px-4 text-xs text-muted-foreground font-medium">
                {lang === 'es' ? 'Conteo' : 'Count'}
              </th>
              <th className="text-right py-2 px-4 text-xs text-muted-foreground font-medium">%</th>
              <th className="py-2 px-4 w-28 text-xs text-muted-foreground font-medium">
                {lang === 'es' ? 'Distribución' : 'Distribution'}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((ev, i) => {
              const label = EVENT_LABELS[ev.name];
              const displayName = label
                ? (lang === 'es' ? label.es : label.en)
                : ev.name.replace(/_/g, ' ');
              const barPct = Math.round((ev.count / total) * 100);
              return (
                <tr
                  key={ev.name}
                  className="border-b border-border/50 hover:bg-muted/40 transition-colors"
                >
                  <td className="py-2 px-4">
                    <span className="text-muted-foreground text-xs mr-2">#{i + 1}</span>
                    <span className="font-medium capitalize">{displayName}</span>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{ev.name}</span>
                  </td>
                  <td className="text-right py-2 px-4 font-mono text-xs">
                    {ev.count.toLocaleString('es-ES')}
                  </td>
                  <td className="text-right py-2 px-4">
                    <Badge variant="outline" className="text-xs">{ev.pct ?? barPct}%</Badge>
                  </td>
                  <td className="py-2 px-4">
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden w-full">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${barPct}%` }}
                        transition={{ duration: 0.5, delay: i * 0.04 }}
                        className="h-full rounded-full bg-gradient-to-r from-violet-600 to-rose-500"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// LiveDataToggle — switch for auto-refresh every 60 seconds
// ---------------------------------------------------------------------------
function LiveDataToggle({ enabled, onToggle, lang, lastRefreshed }) {
  const fmtTime = lastRefreshed
    ? lastRefreshed.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <div className="flex items-center gap-3" data-testid="live-data-toggle">
      <button
        onClick={onToggle}
        data-testid="live-toggle-btn"
        className={cn(
          'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none',
          enabled ? 'bg-violet-600' : 'bg-muted-foreground/30'
        )}
      >
        <span
          className={cn(
            'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform',
            enabled ? 'translate-x-4' : 'translate-x-1'
          )}
        />
      </button>
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Radio className={cn('w-3 h-3', enabled && 'text-violet-500 animate-pulse')} />
        {lang === 'es' ? 'Actualización en vivo' : 'Live refresh'}
        {enabled && fmtTime && (
          <span className="text-muted-foreground/70">· {lang === 'es' ? 'último' : 'last'} {fmtTime}</span>
        )}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Funnel (CSS-based, avoids recharts Funnel instability in v3)
// ---------------------------------------------------------------------------
function FunnelChart({ data, lang }) {
  const max = data[0].value;
  return (
    <Card data-testid="funnel-chart">
      <CardHeader>
        <CardTitle className="text-base">
          {lang === 'es' ? 'Embudo de Conversión' : 'Conversion Funnel'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.map((step, i) => {
          const pct = Math.round((step.value / max) * 100);
          const convRate = i > 0
            ? Math.round((step.value / data[i - 1].value) * 100)
            : 100;
          return (
            <div key={step.name} data-testid={`funnel-step-${i}`}>
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span className="font-medium text-foreground">{step.name}</span>
                <span>
                  {step.value.toLocaleString('es-ES')}
                  {i > 0 && (
                    <span className="ml-2 text-violet-500">↓{convRate}%</span>
                  )}
                </span>
              </div>
              <div className="h-8 rounded-md overflow-hidden bg-muted">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="h-full rounded-md"
                  style={{ backgroundColor: step.fill }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// DeviceDonut
// ---------------------------------------------------------------------------
const RADIAN = Math.PI / 180;
function renderDonutLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x} y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

function DeviceDonut({ data, lang }) {
  return (
    <Card data-testid="device-donut">
      <CardHeader>
        <CardTitle className="text-base">
          {lang === 'es' ? 'Dispositivos' : 'Devices'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={85}
              dataKey="value"
              labelLine={false}
              label={renderDonutLabel}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.5rem',
                fontSize: '12px',
              }}
              formatter={(value) => [`${value}%`, '']}
            />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px' }} />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PokemonBarChart (horizontal)
// ---------------------------------------------------------------------------
function PokemonBarChart({ data, lang }) {
  return (
    <Card data-testid="pokemon-bar-chart">
      <CardHeader>
        <CardTitle className="text-base">
          {lang === 'es' ? 'Top 10 Pokémon Más Vistos' : 'Top 10 Most Viewed Pokémon'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 0, right: 20, bottom: 0, left: 10 }}
          >
            <defs>
              <linearGradient id="barGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#7C3AED" />
                <stop offset="100%" stopColor="#E11D48" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }}
              tickLine={false}
              axisLine={false}
              width={72}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.5rem',
                fontSize: '12px',
              }}
            />
            <Bar dataKey="views" fill="url(#barGrad)" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Cache gauge (SVG arc)
// ---------------------------------------------------------------------------
function CacheGauge({ value }) {
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (value / 100) * circumference;
  return (
    <div className="flex flex-col items-center" data-testid="cache-gauge">
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="40" fill="none" stroke="hsl(var(--muted))" strokeWidth="8" />
        <circle
          cx="50" cy="50" r="40"
          fill="none"
          stroke="#7C3AED"
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
        <text
          x="50" y="54"
          textAnchor="middle"
          fontSize="18"
          fontWeight="700"
          fill="currentColor"
        >
          {value}%
        </text>
      </svg>
      <p className="text-xs text-muted-foreground mt-1">Cache Hit Rate</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton loading state
// ---------------------------------------------------------------------------
function SkeletonDashboard() {
  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="skeleton-dashboard">
      <div className="w-60 bg-card border-r border-border p-4 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-lg" />
        ))}
      </div>
      <div className="flex-1 p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-72 rounded-xl" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tooltip style helper
// ---------------------------------------------------------------------------
const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '0.5rem',
  fontSize: '12px',
};

// ---------------------------------------------------------------------------
// Filter data by date range (supports: today, yesterday, 7d, 30d, month, custom)
// ---------------------------------------------------------------------------
function filterDataByDateRange(dailyData, dateRange, customFrom, customTo) {
  if (!dailyData || !Array.isArray(dailyData) || dailyData.length === 0) return [];

  // Use last entry's ISO date as "today" reference — works with mock (Jan 2026) and real dates
  const lastDay = dailyData[dailyData.length - 1];
  const lastDateISO = lastDay.dateISO || lastDay.date;
  const today = new Date(lastDateISO);
  today.setHours(0, 0, 0, 0);
  const todayISO = today.toISOString().split('T')[0];

  if (dateRange === 'custom' && customFrom && customTo) {
    return dailyData.filter(day => {
      const dayISO = day.dateISO || day.date;
      return dayISO >= customFrom && dayISO <= customTo;
    });
  }

  if (dateRange === 'yesterday') {
    const yest = new Date(today);
    yest.setDate(yest.getDate() - 1);
    const yestISO = yest.toISOString().split('T')[0];
    return dailyData.filter(day => (day.dateISO || day.date) === yestISO);
  }

  let daysToInclude = 30;
  if      (dateRange === 'today') daysToInclude = 1;
  else if (dateRange === '7d')    daysToInclude = 7;
  else if (dateRange === 'month' || dateRange === '30d') daysToInclude = 30;

  const cutoffDate = new Date(today);
  cutoffDate.setDate(cutoffDate.getDate() - daysToInclude + 1);
  const cutoffISO = cutoffDate.toISOString().split('T')[0];

  return dailyData.filter(day => {
    const dayISO = day.dateISO || day.date;
    return dayISO >= cutoffISO && dayISO <= todayISO;
  });
}

// ---------------------------------------------------------------------------
// Tab panels
// ---------------------------------------------------------------------------
function DashboardTab({ lang, data, dateRange, customFrom, customTo }) {
  const allDailyData   = data?.daily_data    || MOCK_DATA.dailyData;
  const hourlyData     = data?.hourly_data   || MOCK_DATA.hourlyData;
  const kpiComp        = data?.kpi_comparisons || MOCK_DATA.kpiComparisons;
  const topEventsData  = data?.top_events    || MOCK_DATA.topEvents;

  const filteredData = filterDataByDateRange(allDailyData, dateRange, customFrom, customTo);
  const last7 = filteredData.slice(-7);

  const totalVisitors = filteredData.reduce((s, d) => s + (d.visitors || 0), 0);
  const totalSessions = filteredData.reduce((s, d) => s + (d.sessions || 0), 0);
  const bounceRate = Math.round(
    filteredData.reduce((s, d) => s + (d.bounceRate || 0), 0) / (filteredData.length || 1)
  );

  // Compute change from first half vs second half
  const midpoint = Math.ceil(filteredData.length / 2);
  const firstHalf  = filteredData.slice(0, midpoint).reduce((s, d) => s + (d.visitors || 0), 0);
  const secondHalf = filteredData.slice(midpoint).reduce((s, d) => s + (d.visitors || 0), 0);
  const visitorsChange = firstHalf > 0
    ? Math.round(((secondHalf - firstHalf) / firstHalf) * 100)
    : 0;

  // For "today" range, use SQL-aggregated KPI comparisons
  const isToday = dateRange === 'today';

  const kpiCards = [
    {
      title: 'Page Views',
      value:          isToday ? kpiComp.today_views    : totalVisitors,
      change:         visitorsChange,
      yesterdayValue: kpiComp.yesterday_views,
      icon:           Users,
      sparklineData:  last7,
      format:         'number',
    },
    {
      title:          lang === 'es' ? 'Sesiones' : 'Sessions',
      value:          isToday ? kpiComp.today_sessions : totalSessions,
      change:         visitorsChange + 2,
      yesterdayValue: kpiComp.yesterday_sessions,
      icon:           Activity,
      sparklineData:  last7,
      format:         'number',
    },
    {
      title:          'Bounce Rate',
      value:          isToday ? kpiComp.today_bounce_rate : (data?.bounce_rate ?? bounceRate),
      change:         isToday
        ? kpiComp.today_bounce_rate - kpiComp.yesterday_bounce_rate
        : -3,
      yesterdayValue: kpiComp.yesterday_bounce_rate,
      icon:           TrendingDown,
      sparklineData:  last7,
      format:         'percent',
    },
    {
      title:          lang === 'es' ? 'Eventos Custom' : 'Custom Events',
      value:          isToday ? kpiComp.today_custom_events : (data?.total_events ?? 0),
      change:         visitorsChange,
      yesterdayValue: kpiComp.yesterday_custom_events,
      icon:           Clock,
      sparklineData:  last7,
      format:         'number',
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpiCards.map((card) => (
          <KPICard key={card.title} {...card} lang={lang} />
        ))}
      </div>

      {/* Hourly comparison chart (Today vs Yesterday) */}
      <HourlyComparisonChart data={hourlyData} lang={lang} />

      {/* Daily traffic area chart */}
      <TrafficChart data={filteredData} lang={lang} />

      {/* Top Events Table */}
      {topEventsData.length > 0 && (
        <TopEventsTable data={topEventsData} lang={lang} />
      )}
    </div>
  );
}

function TrafficTab({ lang, data, dateRange }) {
  const topRoutes = data?.top_routes || MOCK_DATA.topRoutes;
  const funnel = data?.funnel || MOCK_DATA.funnel;
  const referrers = data?.referrers || [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card data-testid="traffic-routes-table">
        <CardHeader>
          <CardTitle className="text-base">
            {lang === 'es' ? 'Rutas Más Visitadas' : 'Top Routes'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-4 text-muted-foreground font-medium">
                  {lang === 'es' ? 'Ruta' : 'Route'}
                </th>
                <th className="text-right py-2 px-4 text-muted-foreground font-medium">
                  {lang === 'es' ? 'Visitas' : 'Visits'}
                </th>
                <th className="text-right py-2 px-4 text-muted-foreground font-medium">%</th>
              </tr>
            </thead>
            <tbody>
              {topRoutes.map((r, i) => (
                <tr
                  key={r.path + i}
                  className="border-b border-border/50 hover:bg-muted/50 transition-colors"
                >
                  <td className="py-2.5 px-4">
                    <div className="font-medium">{r.name || r.path}</div>
                    <div className="text-xs text-muted-foreground font-mono">{r.path}</div>
                  </td>
                  <td className="text-right py-2.5 px-4 font-mono">
                    {r.visits.toLocaleString('es-ES')}
                  </td>
                  <td className="text-right py-2.5 px-4">
                    <Badge variant="outline" className="text-xs">{r.pct}%</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="space-y-6">
        <FunnelChart data={funnel} lang={lang} />

        {referrers.length > 0 && (
          <Card data-testid="referrers-list">
            <CardHeader>
              <CardTitle className="text-base">
                {lang === 'es' ? 'Referrers' : 'Referrers'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {referrers.map((r, i) => (
                <div key={r.domain} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="text-muted-foreground w-4">#{i + 1}</span>
                    <span className="font-mono text-xs">{r.domain}</span>
                  </span>
                  <Badge variant="outline">{r.visits.toLocaleString('es-ES')}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function GeoTab({ lang, data, dateRange }) {
  const countries = data?.countries?.length ? data.countries : MOCK_DATA.countries;
  const devices = data?.devices?.length ? data.devices : MOCK_DATA.devices;
  const browsers = data?.browsers?.length ? data.browsers : MOCK_DATA.browsers;
  const osData = data?.os || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card data-testid="country-list">
        <CardHeader>
          <CardTitle className="text-base">
            {lang === 'es' ? 'Países' : 'Countries'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {countries.map((c) => (
            <div key={c.name}>
              <div className="flex justify-between text-sm mb-1">
                <span>{c.flag} {c.name}</span>
                <span className="font-mono text-muted-foreground">{c.pct}%</span>
              </div>
              <Progress value={c.pct} className="h-1.5" />
            </div>
          ))}
        </CardContent>
      </Card>

      <DeviceDonut data={devices} lang={lang} />

      <div className="space-y-6">
        <Card data-testid="browser-list">
          <CardHeader>
            <CardTitle className="text-base">
              {lang === 'es' ? 'Navegadores' : 'Browsers'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {browsers.map((b) => (
              <div key={b.name}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{b.name}</span>
                  <span className="font-mono text-muted-foreground">{b.pct}%</span>
                </div>
                <Progress value={b.pct} className="h-1.5" />
              </div>
            ))}
          </CardContent>
        </Card>

        {osData.length > 0 && (
          <Card data-testid="os-list">
            <CardHeader>
              <CardTitle className="text-base">
                {lang === 'es' ? 'Sistemas Operativos' : 'Operating Systems'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {osData.map((o) => (
                <div key={o.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{o.name}</span>
                    <span className="font-mono text-muted-foreground">{o.pct}%</span>
                  </div>
                  <Progress value={o.pct} className="h-1.5" />
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function PokemonTab({ lang, data, dateRange }) {
  const topPokemon = data?.top_pokemon?.length ? data.top_pokemon : MOCK_DATA.topPokemon;
  const topMoves = data?.top_moves?.length ? data.top_moves : MOCK_DATA.topMoves;
  const topItems = data?.top_items?.length ? data.top_items : MOCK_DATA.topItems;
  const topSearches = data?.top_searches || [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <PokemonBarChart data={topPokemon} lang={lang} />
      </div>
      <div className="space-y-6">
        <Card data-testid="top-moves-list">
          <CardHeader>
            <CardTitle className="text-base">
              {lang === 'es' ? 'Movimientos Populares' : 'Popular Moves'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {topMoves.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                {lang === 'es' ? 'Sin datos aún - usa la Calculadora de Daño' : 'No data yet - use the Damage Calculator'}
              </p>
            )}
            {topMoves.map((m, i) => (
              <div key={m.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="text-muted-foreground w-4">#{i + 1}</span>
                  <span className="capitalize">{m.name.replace(/-/g, ' ')}</span>
                </span>
                <Badge variant="outline">{m.count.toLocaleString('es-ES')}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="top-items-list">
          <CardHeader>
            <CardTitle className="text-base">
              {lang === 'es' ? 'Objetos Populares' : 'Popular Items'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {topItems.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                {lang === 'es' ? 'Sin datos aún - visita la página de Objetos' : 'No data yet - visit the Items page'}
              </p>
            )}
            {topItems.map((item, i) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="text-muted-foreground w-4">#{i + 1}</span>
                  <span className="capitalize">{item.name.replace(/-/g, ' ')}</span>
                </span>
                <Badge variant="outline">{item.count.toLocaleString('es-ES')}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {topSearches.length > 0 && (
          <Card data-testid="top-searches-list">
            <CardHeader>
              <CardTitle className="text-base">
                {lang === 'es' ? 'Búsquedas Populares' : 'Popular Searches'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {topSearches.map((s, i) => (
                <div key={s.query} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="text-muted-foreground w-4">#{i + 1}</span>
                    <span className="font-mono text-xs">{s.query}</span>
                  </span>
                  <Badge variant="outline">{s.count.toLocaleString('es-ES')}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function HealthTab({ lang, data, dateRange, customFrom, customTo }) {
  const allDailyData = data?.daily_data || MOCK_DATA.dailyData;
  const filteredData = filterDataByDateRange(allDailyData, dateRange, customFrom, customTo);
  const actions = data?.actions || {};
  const totalEvents = data?.total_events || 0;
  const totalPageviews = data?.total_pageviews || 0;
  const activeDays = data?.unique_days_active || 0;

  // Use real sessions data for the daily chart
  const chartData = filteredData.map(d => ({
    ...d,
    sessions: d.sessions || 0,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card data-testid="response-time-chart">
            <CardHeader>
              <CardTitle className="text-base">
                {lang === 'es' ? 'Sesiones Diarias' : 'Daily Sessions'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    interval={6}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    width={35}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line
                    type="monotone"
                    dataKey="sessions"
                    stroke="#7C3AED"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Card data-testid="system-stats">
          <CardHeader>
            <CardTitle className="text-base">
              {lang === 'es' ? 'Resumen del Sistema' : 'System Summary'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{lang === 'es' ? 'Eventos Totales' : 'Total Events'}</span>
              <span className="font-mono font-bold">{totalEvents.toLocaleString('es-ES')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Page Views</span>
              <span className="font-mono font-bold">{totalPageviews.toLocaleString('es-ES')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{lang === 'es' ? 'Días Activos' : 'Active Days'}</span>
              <span className="font-mono font-bold">{activeDays}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Bounce Rate</span>
              <span className="font-mono font-bold">{data?.bounce_rate ?? 0}%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="api-volume-chart">
          <CardHeader>
            <CardTitle className="text-base">
              {lang === 'es' ? 'Visitas Diarias (30d)' : 'Daily Visits (30d)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 5, right: 20, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="apiGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#E11D48" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#E11D48" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  interval={6}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  width={45}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Area
                  type="monotone"
                  dataKey="visitors"
                  stroke="#E11D48"
                  strokeWidth={2}
                  fill="url(#apiGrad)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {Object.keys(actions).length > 0 && (
          <Card data-testid="actions-breakdown">
            <CardHeader>
              <CardTitle className="text-base">
                {lang === 'es' ? 'Acciones del Usuario' : 'User Actions'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(actions)
                .sort(([, a], [, b]) => b - a)
                .map(([action, count]) => (
                  <div key={action} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-xs capitalize">{action.replace(/_/g, ' ')}</span>
                    <Badge variant="outline">{count.toLocaleString('es-ES')}</Badge>
                  </div>
                ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auth Gate
// ---------------------------------------------------------------------------
function AnalyticsAuthGate({ onLogin }) {
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onLogin(password);
    } catch {
      setError(true);
      setPassword('');
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-rose-500 flex items-center justify-center mb-3">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-xl">
              <span className="text-violet-600 dark:text-violet-400">Dex</span>
              <span className="text-rose-500 dark:text-rose-400">Riddle</span>
              {' '}Analytics
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Área restringida · Ingresa el token de acceso
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input
                  ref={inputRef}
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Token de acceso"
                  className={cn(
                    'w-full bg-background border rounded-lg px-4 py-2.5 pr-10 text-sm outline-none transition-colors',
                    error
                      ? 'border-rose-500 animate-pulse'
                      : 'border-input focus:border-violet-500'
                  )}
                  data-testid="analytics-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {error && (
                <p className="text-xs text-rose-500 text-center">Token incorrecto. Intenta de nuevo.</p>
              )}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-violet-600 to-rose-500 text-white hover:from-violet-700 hover:to-rose-600"
                data-testid="analytics-login-btn"
              >
                Acceder al Dashboard
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Real data hook - fetches from backend with optional live refresh
// ---------------------------------------------------------------------------
const LIVE_REFRESH_INTERVAL = 60 * 1000; // 60 seconds

function useRealAnalytics(authed, liveEnabled, token) {
  const [realData, setRealData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);

  const fetch_data = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch('/api/analytics/data', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRealData(data);
        setLastRefreshed(new Date());
      } else {
        setFetchError(
          `Error ${res.status}: ${
            res.status === 401
              ? 'Token incorrecto o sesión expirada. Cierra sesión y vuelve a iniciar.'
              : res.statusText
          }`
        );
      }
    } catch (err) {
      setFetchError(
        `No se pudo conectar al backend. Verifica CORS y que el servidor esté activo.`
      );
      console.error('[Analytics] fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [authed, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial fetch on auth
  useEffect(() => {
    fetch_data();
  }, [fetch_data]);

  // Live auto-refresh interval
  useEffect(() => {
    if (liveEnabled && authed) {
      intervalRef.current = setInterval(fetch_data, LIVE_REFRESH_INTERVAL);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [liveEnabled, authed, fetch_data]);

  return { realData, loading, fetchError, lastRefreshed, refresh: fetch_data };
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------
export default function AdminAnalytics() {
  const { language } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sidebarExpanded, setSidebarExpanded] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, token, login } = useAdminAuth();
  const [liveEnabled, setLiveEnabled] = useState(false);

  // Date range — stored in URL so it's shareable
  const activeTab   = searchParams.get('tab')   || 'dashboard';
  const dateRange   = searchParams.get('range') || '30d';
  const customFrom  = searchParams.get('from')  || '';
  const customTo    = searchParams.get('to')    || '';

  const setTab = (tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const setDateRange = (range) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('range', range);
      return next;
    });
  };

  const setCustomFrom = (val) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('from', val);
      next.set('range', 'custom');
      return next;
    });
  };

  const setCustomTo = (val) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('to', val);
      next.set('range', 'custom');
      return next;
    });
  };

  const lang = language || 'es';

  const { realData, loading: realLoading, fetchError, lastRefreshed, refresh } =
    useRealAnalytics(isAuthenticated, liveEnabled, token);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  if (!isAuthenticated) {
    return <AnalyticsAuthGate onLogin={login} />;
  }

  if (isLoading) return <SkeletonDashboard />;

  return (
    <div
      className="flex overflow-hidden"
      style={{ height: 'calc(100vh - 4rem)' }}
      data-testid="admin-analytics"
    >
      <SidebarNav
        activeTab={activeTab}
        setTab={setTab}
        expanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded((v) => !v)}
        lang={lang}
      />

      <main className="flex-1 overflow-y-auto bg-background">
        <div className="p-6">
          {/* Header row */}
          <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold">
                <span className="text-violet-600 dark:text-violet-400">Analytics</span>
                <span className="text-muted-foreground font-normal text-base ml-2">
                  · DexRiddle BI
                </span>
              </h1>
              {realData ? (
                <p className="text-xs text-green-500 mt-0.5 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                  {lang === 'es' ? 'Datos reales' : 'Live data'} ·{' '}
                  {realData.total_pageviews.toLocaleString()} page views ·{' '}
                  {realData.total_events.toLocaleString()} {lang === 'es' ? 'eventos' : 'events'}
                </p>
              ) : realLoading ? (
                <p className="text-xs text-blue-500 mt-0.5 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-blue-500 inline-block animate-pulse" />
                  {lang === 'es' ? 'Cargando datos en tiempo real...' : 'Loading live data...'}
                </p>
              ) : fetchError ? (
                <p className="text-xs text-rose-500 mt-0.5 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-rose-500 inline-block" />
                  {fetchError}
                </p>
              ) : (
                <p className="text-xs text-amber-500 mt-0.5 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                  {lang === 'es' ? 'Datos de demostración (sin tráfico real aún)' : 'Demo data (no real traffic yet)'}
                </p>
              )}
            </div>

            {/* Controls: Live toggle + manual refresh */}
            <div className="flex items-center gap-4">
              <LiveDataToggle
                enabled={liveEnabled}
                onToggle={() => setLiveEnabled((v) => !v)}
                lang={lang}
                lastRefreshed={lastRefreshed}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={realLoading}
                className="gap-2 text-xs"
                data-testid="refresh-btn"
              >
                <RefreshCw className={cn('w-3 h-3', realLoading && 'animate-spin')} />
                {lang === 'es' ? 'Actualizar' : 'Refresh'}
              </Button>
            </div>
          </div>

          {/* Date range picker — persisted in URL */}
          <DateRangePicker
            lang={lang}
            active={dateRange}
            setActive={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            setCustomFrom={setCustomFrom}
            setCustomTo={setCustomTo}
          />

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
            >
              {activeTab === 'dashboard' && (
                <DashboardTab lang={lang} data={realData} dateRange={dateRange} customFrom={customFrom} customTo={customTo} />
              )}
              {activeTab === 'traffic' && (
                <TrafficTab lang={lang} data={realData} dateRange={dateRange} />
              )}
              {activeTab === 'geo' && (
                <GeoTab lang={lang} data={realData} dateRange={dateRange} />
              )}
              {activeTab === 'pokemon' && (
                <PokemonTab lang={lang} data={realData} dateRange={dateRange} />
              )}
              {activeTab === 'health' && (
                <HealthTab lang={lang} data={realData} dateRange={dateRange} customFrom={customFrom} customTo={customTo} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
